import React, { useEffect, useState } from "react";
import {
  getOrganizaciones,
  postOrganizacion,
  putOrganizacion,
  deleteOrganizacion,
} from "../../componentes/http/actions/OrganizacionAction";
import { getClientes } from "../../componentes/http/actions/ClientesAction";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Grid,
  Typography,
  Box,
  Snackbar,
  Alert,
  TextField,
  TablePagination,
  InputAdornment,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import NuevaOrganizacionModal from "../../componentes/modal/Organizaciones/NuevaOrganizacionModal";
import EditarOrganizacionModal from "../../componentes/modal/Organizaciones/EditarOrganizacionModal";

const GestorOrganizaciones = () => {
  const [organizaciones, setOrganizaciones] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [organizacionSeleccionada, setOrganizacionSeleccionada] = useState(null);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [organizacionAEliminar, setOrganizacionAEliminar] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const [orgs, cls] = await Promise.all([
        getOrganizaciones(token),
        getClientes(token),
      ]);
      setOrganizaciones(orgs);
      setClientes(cls);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      setInitialDataLoaded(true);
    }
  };

  const filteredOrganizaciones = organizaciones.filter((org) => {
    const term = searchTerm.toLowerCase();
    return (
      org.dominio?.toLowerCase().includes(term) ||
      org.cliente?.nombre?.toLowerCase().includes(term) ||
      org.proveedorSSO?.toLowerCase().includes(term)
    );
  });

  const displayedOrganizaciones = filteredOrganizaciones.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleNuevaOrganizacion = async (dto) => {
    try {
      setIsLoading(true);
      await postOrganizacion(localStorage.getItem("token"), dto);
      await fetchData();
      setOpenModal(false);
      setSnackbar({ open: true, message: "Organización creada con éxito", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error?.response?.data || "Error al crear organización", severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditarOrganizacion = async (dto) => {
    try {
      setIsLoading(true);
      await putOrganizacion(localStorage.getItem("token"), dto);
      await fetchData();
      setOpenEditModal(false);
      setOrganizacionSeleccionada(null);
      setSnackbar({ open: true, message: "Organización editada con éxito", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error?.response?.data || "Error al editar organización", severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDelete = (org) => {
    setOrganizacionAEliminar(org);
    setOpenConfirmDelete(true);
  };

  const handleEliminarOrganizacion = async () => {
    try {
      setIsLoading(true);
      await deleteOrganizacion(localStorage.getItem("token"), organizacionAEliminar.id);
      await fetchData();
      setSnackbar({ open: true, message: "Organización eliminada con éxito", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error?.response?.data || "Error al eliminar organización", severity: "error" });
    } finally {
      setIsLoading(false);
      setOpenConfirmDelete(false);
      setOrganizacionAEliminar(null);
    }
  };

  return (
    <Grid container direction="column" spacing={2} sx={{ paddingTop: { xs: "20px", sm: "10px" } }}>
      <Grid item>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h4">Organizaciones SSO</Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenModal(true)}
              disabled={isLoading}
              startIcon={<AddIcon />}
            >
              Nueva organización
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid item>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar por dominio, cliente o proveedor..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid item>
        {isLoading && !initialDataLoaded ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : displayedOrganizaciones.length > 0 ? (
          <>
            <TableContainer component={Paper}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Dominio</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Proveedor SSO</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedOrganizaciones.map((org) => (
                    <TableRow key={org.id} hover>
                      <TableCell>{org.cliente?.nombre ?? "-"}</TableCell>
                      <TableCell>{org.dominio}</TableCell>
                      <TableCell>
                        <Chip
                          label={org.proveedorSSO}
                          size="small"
                          color={org.proveedorSSO === "Microsoft" ? "primary" : "default"}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={org.habilitada ? "Habilitada" : "Deshabilitada"}
                          size="small"
                          color={org.habilitada ? "success" : "error"}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                          <Tooltip title="Editar organización">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                setOrganizacionSeleccionada(org);
                                setOpenEditModal(true);
                              }}
                            >
                              <EditNoteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar organización">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleConfirmDelete(org)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredOrganizaciones.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        ) : (
          <TableContainer component={Paper}>
            <Box p={3}>
              <Typography variant="body1" color="text.secondary">
                No se encontraron organizaciones con los criterios de búsqueda actuales.
              </Typography>
            </Box>
          </TableContainer>
        )}
      </Grid>

      <NuevaOrganizacionModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSubmit={handleNuevaOrganizacion}
        clientes={clientes}
        isLoading={isLoading}
      />

      <EditarOrganizacionModal
        open={openEditModal}
        onClose={() => { setOpenEditModal(false); setOrganizacionSeleccionada(null); }}
        onSubmit={handleEditarOrganizacion}
        organizacion={organizacionSeleccionada}
        clientes={clientes}
        isLoading={isLoading}
      />

      <Dialog open={openConfirmDelete} onClose={() => setOpenConfirmDelete(false)}>
        <DialogTitle>Eliminar organización</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar la organización con dominio{" "}
            <strong>{organizacionAEliminar?.dominio}</strong>? Los usuarios que se hayan
            registrado mediante esta organización no serán eliminados.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDelete(false)}>Cancelar</Button>
          <Button onClick={handleEliminarOrganizacion} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default GestorOrganizaciones;